/* eslint-disable react/prop-types */
import React from 'react';
import { Check, ChevronRight } from 'lucide-react';

import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { C, F } from '../constants/theme';
import { Btn, CheckBox, Heading, OptionCard, Radio, Sub, Tag } from '../components';
import { toggleArr } from '../helpers';
import { Loading } from 'core/ui/components';

import useListPrograms from 'core/hooks/useListPrograms';
import { useProgramContext } from 'core/api/programContext';
import {
  LIST_DIETS,
  // LIST_PROTEINS,
  LIST_CUISINES,
  LIST_ONRAMP_FOOD_GROUPS,
} from 'core/api/apollo/queries';
import {
  SET_DIET,
  SET_CUISINE_PREFERENCES,
  SET_FAVORITE_PROTEINS,
  SET_FOODS_TO_AVOID,
} from 'core/api/apollo/mutations';

const FOOD_GROUP_INFO = {
  'nightshades': { displayName: 'Nightshades', description: 'Tomato, pepper, eggplant, potato' },
  'eggs': { displayName: 'Eggs', description: 'All egg preparations' },
  'dairy': { displayName: 'Dairy', description: 'Yogurt, butter, cheese, cream' },
  'soy': { displayName: 'Soy', description: 'Soy sauce, tofu, edamame' },
  'tree-nuts': { displayName: 'Tree Nuts', description: 'Almonds, cashews, walnuts' },
  'coconut': { displayName: 'Coconut', description: 'Coconut oil, milk, cream, flakes' },
  'shellfish': { displayName: 'Shellfish', description: 'Shrimp, crab, lobster, scallops' },
  'pork': { displayName: 'Pork', description: 'All pork products' },
  'red-meat': { displayName: 'Red Meat', description: 'Beef, lamb, bison' },
  'mushrooms': { displayName: 'Mushrooms', description: 'All varieties' },
  'alliums': { displayName: 'Alliums', description: 'Onion, garlic, shallot, leek' },
  'corn': { displayName: 'Corn', description: 'Corn, cornstarch' },
  'sesame': { displayName: 'Sesame', description: 'Sesame seeds, tahini, sesame oil' },
  'cilantro': { displayName: 'Cilantro', description: 'Fresh cilantro / coriander leaf' },
};

const Phase3Taste = ({
  subStep,
  goPhase,
}) => {
  const { loading, signatureProgramList } = useListPrograms();
  const {
    selectedDiet, setSelectedDiet, setSelectedProgram,
    cuisines, setCuisines,
    proteins, setProteins,
    avoidances, setAvoidances,
  } = useProgramContext();
  const { data: { listDiets = [] } = {} } = useQuery(LIST_DIETS);
  const { data: { listCuisines = [] } = {} } = useQuery(LIST_CUISINES);
  const { data: { listOnrampFoodGroups = [] } = {} } = useQuery(LIST_ONRAMP_FOOD_GROUPS, {
    variables: { programName: selectedDiet?.name },
    skip: !selectedDiet?.name,
  });

  const [setFoodsToAvoid] = useMutation(SET_FOODS_TO_AVOID, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Foods to avoid set successfully:', data);
      goPhase(4);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting foods to avoid:', error);
    },
  });

  const getTagIdsForSelectedGroups = () => {
    return listOnrampFoodGroups
      .filter((g) => avoidances.includes(g.key))
      .flatMap((g) => g.ingredientTags.map((t) => t.id));
  };

  const handleSetFoodsToAvoid = async () => {
    try {
      await setFoodsToAvoid({
        variables: {
          input: {
            foodsToAvoidIds: getTagIdsForSelectedGroups(),
          },
        },
      });
    } catch (error) {
      console.error('Failed to set foods to avoid:', error);
      throw error;
    }
  };

  const [setFavoriteProteins] = useMutation(SET_FAVORITE_PROTEINS, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Favorite proteins set successfully:', data);
      goPhase(3, 3);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting favorite proteins:', error);
    },
  });

  const handleSetFavoriteProteins = async () => {
    try {
      await setFavoriteProteins({
        variables: {
          input: {
            ingredientTagIds: proteins,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set favorite proteins:', error);
      throw error;
    }
  };

  const [setDiet] = useMutation(SET_DIET, {
    onCompleted: () => {
      goPhase(3, 1);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in Diet Set:', error);
    },
  });

  // eslint-disable-next-line no-console
  console.log('selectedDiet', selectedDiet);
  // eslint-disable-next-line no-console
  // console.log('listDiets', listDiets);
  // eslint-disable-next-line no-console
  console.log('signatureProgramList', signatureProgramList);

  // Maps frontend PrepaidProgram internalNames to backend Diet names where they differ
  const dietNameMap = {
    'seafood+vegetarian': 'pescatarian',
    'pescatarian': 'pescatarian',
  };

  const handleSetDiet = async () => {
    const internalName = selectedDiet?.internalName?.toLowerCase() || '';
    const lookupName = dietNameMap[internalName] || internalName;
    const matchingDiet = listDiets.find(
      (d) => d.name.toLowerCase() === lookupName
    );
    if (!matchingDiet) {
      // Omnivore ("Meat + Seafood") has no dietary restriction — skip SET_DIET and advance
      goPhase(3, 1);
      return;
    }
    try {
      await setDiet({
        variables: {
          input: {
            dietId: matchingDiet.id,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set diet:', error);
      throw error;
    }
  };

  const [setCuisinePreferences] = useMutation(SET_CUISINE_PREFERENCES, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Cuisine preferences set successfully:', data);
      goPhase(3, 2);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting cuisine preferences:', error);
    },
  });

  const handleSetCuisinePreferences = async () => {
    try {
      await setCuisinePreferences({
        variables: {
          input: {
            cuisineIds: cuisines,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set cuisine preferences:', error);
      throw error;
    }
  };

  const iconList = {
    'meat+seafood': { internalName: 'Meat & Seafood', icon: 'steak', description: 'The full menu', tag: 'MOST POPULAR' },
    'meat+vegetarian': { internalName: 'Meat & Vegetarian', icon: 'carrot', description: 'No seafood' },
    'seafood+vegetarian': { internalName: 'Seafood & Vegetarian', icon: 'fish', description: 'No land animal protein' },
    'vegetarian': { internalName: 'Vegetarian', icon: 'leaf', description: 'No meat, no seafood' },
    'pescatarian': { internalName: 'Pescatarian', icon: 'fish', description: 'Seafood + vegetarian, no land animal protein' },
    'Other': { icon: 'steak', description: 'Others, including omnivore, flexitarian, etc.' },
  };

  if (loading) {
    return <Loading />;
  }

  const handleSetDietOption = (option) => {
    setSelectedDiet(option);
    setSelectedProgram(null);
  };

  return <>
    {subStep === 0 &&
      <>
        <Heading>How do you like to eat?</Heading>
        <Sub>What you eat, what you love, and what to leave out. We don't currently offer a vegan program.</Sub>
        {/* <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 10 }}>
          How do you like to eat?
        </div> */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {signatureProgramList.map((o) =>
            <OptionCard key={o.id} active={selectedDiet?.id === o.id} onClick={() => handleSetDietOption(o)}>
              {/* <IconBox name={iconList[o.internalName]?.icon || 'steak'} active={selectedDiet?.id === o.id}/> */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown }}>
                  {iconList[o.internalName]?.internalName || o.name}
                </div>
                <div style={{ fontSize: 11, color: C.lightBrown }}>{iconList[o.internalName]?.description}</div>
              </div>
              {iconList[o.internalName]?.tag && <Tag color={C.mustard}>{iconList[o.internalName]?.tag}</Tag>}
              <Radio active={selectedDiet?.id === o?.id}/>
            </OptionCard>
          )}
        </div>
        <Btn
          onClick={handleSetDiet}
          disabled={!selectedDiet}
          style={{ width: '100%', marginTop: 16 }}>
          Continue <ChevronRight size={15}/>
        </Btn>
      </>
    }
    {subStep === 1 && <>
      <Heading>What cuisines speak to you?</Heading>
      <Sub>Check all that apply. The algorithm boosts meals from these cuisines.</Sub>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {listCuisines && listCuisines.map((c) =>
          <OptionCard key={c.id} active={cuisines.includes(c.id)} onClick={() => toggleArr(cuisines, setCuisines, c.id)}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{c.name}</div>
              <div style={{ fontSize: 10.5, color: C.lightBrown }}>{c.description}</div>
            </div>
            <CheckBox active={cuisines.includes(c.id)}/>
          </OptionCard>
        )}
      </div>
      <Btn onClick={handleSetCuisinePreferences} style={{ width: '100%', marginTop: 16 }}>
        {cuisines.length > 0 ? 'Continue' : 'No preference, surprise me'} <ChevronRight size={15}/>
      </Btn>
    </>}
    {subStep === 2 && selectedDiet?.proteins && selectedDiet?.internalName !== 'vegetarian' && <>
      {/* Skip if selectedDiet?.name is Vegetarian */}
      <Heading>Favorite proteins?</Heading>
      <Sub>All proteins are pastured, wild, and responsibly sourced.</Sub>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {/* {(eatStyle?.name === 'Pescatarian' ? FISH_PROTEINS : eatStyle === 'meat_veg' ? MEAT_PROTEINS : ALL_PROTEINS).map((p) => <button key={p} onClick={() => toggleArr(proteins, setProteins, p)} style={{ padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${proteins.includes(p) ? C.green : '#E8E4DC'}`, background: proteins.includes(p) ? C.optimalBg : C.pureWhite, fontSize: 13, fontWeight: 500, color: C.darkBrown, cursor: 'pointer', fontFamily: F.b }}>{p}</button>)} */}
        {selectedDiet?.proteins.map((p) =>
          <button key={p.id}
            onClick={() => toggleArr(proteins, setProteins, p.id)}
            style={{
              padding: '8px 14px', borderRadius: 20,
              border: `1.5px solid ${proteins.includes(p?.id) ? C.green : '#E8E4DC'}`,
              background: proteins.includes(p?.id) ? C.optimalBg : C.pureWhite,
              textTransform: 'capitalize',
              fontSize: 13,
              fontWeight: 500,
              color: C.darkBrown, cursor: 'pointer', fontFamily: F.b,
            }}>{p?.name}
            </button>
          )
        }
      </div>
      <Btn onClick={handleSetFavoriteProteins}  style={{ width: '100%', marginTop: 16 }}>
        {proteins.length > 0 ? 'Continue' : 'I like everything'} <ChevronRight size={15}/>
      </Btn>
    </>}
    {(subStep === 3 || (subStep === 2 && selectedDiet?.internalName === 'vegetarian')) && (() => {
      const isVegetarian = selectedDiet?.internalName === 'vegetarian';
      const maxAvoidances = isVegetarian ? 1 : 3;
      return <>
        <Heading>Anything to avoid?</Heading>
        <Sub>Beyond our standard exclusions, are there ingredients we should keep out? {isVegetarian ? 'Select 1.' : 'Select up to 3.'}</Sub>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {['Gluten-Free', 'No Refined Sugar', 'No Canola Oil'].map((t) => <Tag key={t} color={C.green}><Check size={10} strokeWidth={3}/> {t}</Tag>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {listOnrampFoodGroups.map((g) =>
            <OptionCard key={g.key} active={avoidances.includes(g.key)} onClick={() => toggleArr(avoidances, setAvoidances, g.key, maxAvoidances)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{FOOD_GROUP_INFO[g.key]?.displayName || g.key}</div>
                <div style={{ fontSize: 10.5, color: C.lightBrown }}>{FOOD_GROUP_INFO[g.key]?.description}</div>
              </div>
              <CheckBox active={avoidances.includes(g.key)}/>
            </OptionCard>
          )}
        </div>
        <Btn onClick={handleSetFoodsToAvoid} disabled={isVegetarian && avoidances.length !== 1} style={{ width: '100%', marginTop: 16 }}>
          {avoidances.length > 0 ? `Continue with ${avoidances.length} exclusion(s)` : 'No additional exclusions'}
          <ChevronRight size={15}/>
        </Btn>
      </>;
    })()}
  </>;
};

Phase3Taste.propTypes = {
  goPhase: PropTypes.func.isRequired,
};

export default Phase3Taste;
